import * as React from 'react'
import type { CSSProperties } from 'react'
import { Slot } from '@radix-ui/react-slot'

import { cn } from '@/lib/utils'
import { validateIconButton } from '@/shared/lib/accessibility-validator'

/**
 * Button primitive.
 *
 * Variants map directly to the design system:
 *   - `primary`   - filled primary (design-spec "filled primary")
 *   - `secondary` - outlined secondary (design-spec "outlined secondary")
 *   - `ghost`     - ghost tertiary (design-spec "ghost tertiary")
 *   - `danger`    - destructive actions (rejections, deletes)
 *   - `success`   - positive actions (approve)
 *   - `link`      - anchor-style button
 */

// Include 'default' and 'destructive' as aliases for backward compatibility
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'link' | 'default' | 'destructive'
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  asChild?: boolean
}

// Map variant aliases to their canonical names
function normalizeVariant(variant: ButtonVariant): 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'link' {
  if (variant === 'default') return 'primary'
  if (variant === 'destructive') return 'danger'
  return variant
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', asChild = false, style, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    const normalizedVariant = normalizeVariant(variant)

    // Development-only accessibility validation for icon buttons
    // (validateIconButton internally checks isDev())
    if (size === 'icon' && !asChild) {
      const hasLabel = !!(props['aria-label'] || props['aria-labelledby'])
      validateIconButton(hasLabel, 'Button')
    }

    const combinedStyle: CSSProperties = {
      ...baseStyles.button,
      ...variantStyles[normalizedVariant],
      ...sizeStyles[size],
      ...(disabled ? disabledStyles : {}),
      ...style,
    }

    // Map variant to className for hover states
    const hoverClassMap: Record<typeof normalizedVariant, string> = {
      primary: 'button-primary',
      secondary: 'button-secondary',
      ghost: 'button-ghost',
      link: 'button-link',
      danger: 'button-danger',
      success: 'button-success',
    }
    const hoverClass = hoverClassMap[normalizedVariant]

    return (
      <Comp
        ref={ref}
        style={combinedStyle}
        className={cn(hoverClass, className)}
        disabled={disabled}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

const baseStyles: Record<string, CSSProperties> = {
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    borderRadius: 'var(--radius-sm)',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
    border: 'none',
    outline: 'none',
    whiteSpace: 'nowrap',
  },
}

const variantStyles: Record<'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'link', CSSProperties> = {
  primary: {
    background: 'var(--accent-primary)',
    color: 'white',
  },
  secondary: {
    background: 'var(--bg-tertiary)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-default)',
  },
  ghost: {
    background: 'transparent',
    color: 'var(--text-primary)',
  },
  danger: {
    background: 'var(--error)',
    color: 'white',
  },
  success: {
    background: 'var(--success)',
    color: 'white',
  },
  link: {
    background: 'transparent',
    color: 'var(--accent-primary)',
    textDecoration: 'none',
  },
}

const sizeStyles: Record<ButtonSize, CSSProperties> = {
  sm: { height: '32px', padding: '0 12px', fontSize: '13px' },
  md: { height: '36px', padding: '0 16px' },
  lg: { height: '40px', padding: '0 20px', fontSize: '16px' },
  icon: { height: '36px', width: '36px', padding: 0 },
}

const disabledStyles: CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed',
  pointerEvents: 'none',
}
